import { defineDomainTranslations } from './defineDomainTranslations';

const en = {
  'projectsList.dropHere': 'Drop it here',
  'projectsList.gridView': 'Grid view',
  'projectsList.listView': 'List view',
  'projectsList.menu.delete': 'Delete',
  'projectsList.menu.download': 'Download video',
  'projectsList.menu.move': 'Move project',
  'projectsList.menu.rename': 'Rename',
  'projectsList.menu.share': 'Share',
  'projectsList.meta': '{type} · {time}',
  'projectsList.moreActions': 'More actions for {name}',
  'projectsList.restoringFiles':
    'Everything is safe. Your project is temporarily unavailable and will be ready in 3–4 hours.',
  'projectsList.select': 'Select {name}',
  'translationSection.status.analyzingSpeakers': 'Analyzing speakers',
  'translationSection.status.analyzingSrt': 'Analyzing SRT',
  'translationSection.status.analyzingVideo': 'Analyzing video',
  'translationSection.status.analyzingVoices': 'Analyzing voices',
  'translationSection.status.determineSpeakersFailed': 'Speaker identification failed',
  'translationSection.status.error': 'Error',
  'translationSection.status.forbiddenLink': 'Forbidden link',
  'translationSection.status.generatingVideo': 'Generating video',
  'translationSection.status.mergingFailed': 'Merging failed',
  'translationSection.status.noAudio': 'No audio',
  'translationSection.status.noWords': 'No words',
  'translationSection.status.readyToGenerate': 'Ready to generate video',
  'translationSection.status.separateBackgroundFailed': 'Background separation failed',
  'translationSection.status.transcribeSegmentsFailed': 'Segment transcription failed',
  'translationSection.status.transcribing': 'Transcribing video',
  'translationSection.status.transcriptionFailed': 'Transcription failed',
  'translationSection.status.translatingText': 'Translating text',
  'translationSection.status.translationFailed': 'Translation failed',
  'translationSection.status.uploadFailed': 'Upload failed',
  'translationSection.status.uploading': 'Uploading',
  'translationSection.status.videoGenerated': 'Video generated',
  'translationSection.status.videoHasNoSound': 'Video has no sound',
  'translationSection.status.videoLacksSpeech': 'Video lacks speech',
  'translationSection.status.voiceoverFailed': 'Voiceover failed',
  'translationSection.type': 'Translation',
  'translationSection.zeroState.action': 'Upload video',
  'translationSection.zeroState.description':
    'Upload a video and get accurate dubbing, subtitles and voice cloning in 135+ languages.',
  'translationSection.zeroState.title': 'Translate your first video',
} as const;

type Translation = Record<keyof typeof en, string>;

const es = {
  'projectsList.dropHere': 'Suéltalo aquí',
  'projectsList.gridView': 'Vista de cuadrícula',
  'projectsList.listView': 'Vista de lista',
  'projectsList.menu.delete': 'Eliminar',
  'projectsList.menu.download': 'Descargar vídeo',
  'projectsList.menu.move': 'Mover proyecto',
  'projectsList.menu.rename': 'Cambiar nombre',
  'projectsList.menu.share': 'Compartir',
  'projectsList.meta': '{type} · {time}',
  'projectsList.moreActions': 'Más acciones para {name}',
  'projectsList.restoringFiles':
    'Todo está a salvo. Tu proyecto no está disponible temporalmente y estará listo en 3–4 horas.',
  'projectsList.select': 'Seleccionar {name}',
  'translationSection.status.analyzingSpeakers': 'Analizando hablantes',
  'translationSection.status.analyzingSrt': 'Analizando SRT',
  'translationSection.status.analyzingVideo': 'Analizando vídeo',
  'translationSection.status.analyzingVoices': 'Analizando voces',
  'translationSection.status.determineSpeakersFailed': 'Error al identificar hablantes',
  'translationSection.status.error': 'Error',
  'translationSection.status.forbiddenLink': 'Enlace no permitido',
  'translationSection.status.generatingVideo': 'Generando vídeo',
  'translationSection.status.mergingFailed': 'Error al unir el vídeo',
  'translationSection.status.noAudio': 'Sin audio',
  'translationSection.status.noWords': 'Sin palabras',
  'translationSection.status.readyToGenerate': 'Listo para generar el vídeo',
  'translationSection.status.separateBackgroundFailed': 'Error al separar el fondo',
  'translationSection.status.transcribeSegmentsFailed': 'Error al transcribir segmentos',
  'translationSection.status.transcribing': 'Transcribiendo vídeo',
  'translationSection.status.transcriptionFailed': 'Error de transcripción',
  'translationSection.status.translatingText': 'Traduciendo texto',
  'translationSection.status.translationFailed': 'Error de traducción',
  'translationSection.status.uploadFailed': 'Error al subir',
  'translationSection.status.uploading': 'Subiendo',
  'translationSection.status.videoGenerated': 'Vídeo generado',
  'translationSection.status.videoHasNoSound': 'El vídeo no tiene sonido',
  'translationSection.status.videoLacksSpeech': 'El vídeo no tiene voz',
  'translationSection.status.voiceoverFailed': 'Error en la locución',
  'translationSection.type': 'Traducción',
  'translationSection.zeroState.action': 'Subir vídeo',
  'translationSection.zeroState.description':
    'Sube un vídeo y obtén un doblaje preciso, subtítulos y clonación de voz en más de 135 idiomas.',
  'translationSection.zeroState.title': 'Traduce tu primer vídeo',
} satisfies Translation;

const pt = {
  'projectsList.dropHere': 'Solte aqui',
  'projectsList.gridView': 'Visualização em grade',
  'projectsList.listView': 'Visualização em lista',
  'projectsList.menu.delete': 'Excluir',
  'projectsList.menu.download': 'Baixar vídeo',
  'projectsList.menu.move': 'Mover projeto',
  'projectsList.menu.rename': 'Renomear',
  'projectsList.menu.share': 'Compartilhar',
  'projectsList.meta': '{type} · {time}',
  'projectsList.moreActions': 'Mais ações para {name}',
  'projectsList.restoringFiles':
    'Está tudo seguro. Seu projeto está temporariamente indisponível e ficará pronto em 3–4 horas.',
  'projectsList.select': 'Selecionar {name}',
  'translationSection.status.analyzingSpeakers': 'Analisando falantes',
  'translationSection.status.analyzingSrt': 'Analisando SRT',
  'translationSection.status.analyzingVideo': 'Analisando vídeo',
  'translationSection.status.analyzingVoices': 'Analisando vozes',
  'translationSection.status.determineSpeakersFailed': 'Falha ao identificar falantes',
  'translationSection.status.error': 'Erro',
  'translationSection.status.forbiddenLink': 'Link não permitido',
  'translationSection.status.generatingVideo': 'Gerando vídeo',
  'translationSection.status.mergingFailed': 'Falha ao juntar o vídeo',
  'translationSection.status.noAudio': 'Sem áudio',
  'translationSection.status.noWords': 'Sem palavras',
  'translationSection.status.readyToGenerate': 'Pronto para gerar o vídeo',
  'translationSection.status.separateBackgroundFailed': 'Falha ao separar o fundo',
  'translationSection.status.transcribeSegmentsFailed': 'Falha ao transcrever segmentos',
  'translationSection.status.transcribing': 'Transcrevendo vídeo',
  'translationSection.status.transcriptionFailed': 'Falha na transcrição',
  'translationSection.status.translatingText': 'Traduzindo texto',
  'translationSection.status.translationFailed': 'Falha na tradução',
  'translationSection.status.uploadFailed': 'Falha no envio',
  'translationSection.status.uploading': 'Enviando',
  'translationSection.status.videoGenerated': 'Vídeo gerado',
  'translationSection.status.videoHasNoSound': 'O vídeo não tem som',
  'translationSection.status.videoLacksSpeech': 'O vídeo não tem fala',
  'translationSection.status.voiceoverFailed': 'Falha na locução',
  'translationSection.type': 'Tradução',
  'translationSection.zeroState.action': 'Enviar vídeo',
  'translationSection.zeroState.description':
    'Envie um vídeo e receba dublagem precisa, legendas e clonagem de voz em mais de 135 idiomas.',
  'translationSection.zeroState.title': 'Traduza seu primeiro vídeo',
} satisfies Translation;

const fr = {
  'projectsList.dropHere': 'Déposez-le ici',
  'projectsList.gridView': 'Vue grille',
  'projectsList.listView': 'Vue liste',
  'projectsList.menu.delete': 'Supprimer',
  'projectsList.menu.download': 'Télécharger la vidéo',
  'projectsList.menu.move': 'Déplacer le projet',
  'projectsList.menu.rename': 'Renommer',
  'projectsList.menu.share': 'Partager',
  'projectsList.meta': '{type} · {time}',
  'projectsList.moreActions': 'Plus d’actions pour {name}',
  'projectsList.restoringFiles':
    'Vos données sont en sécurité. Votre projet est temporairement indisponible et sera prêt dans 3 à 4 heures.',
  'projectsList.select': 'Sélectionner {name}',
  'translationSection.status.analyzingSpeakers': 'Analyse des intervenants',
  'translationSection.status.analyzingSrt': 'Analyse du SRT',
  'translationSection.status.analyzingVideo': 'Analyse de la vidéo',
  'translationSection.status.analyzingVoices': 'Analyse des voix',
  'translationSection.status.determineSpeakersFailed': 'Échec de l’identification des intervenants',
  'translationSection.status.error': 'Erreur',
  'translationSection.status.forbiddenLink': 'Lien interdit',
  'translationSection.status.generatingVideo': 'Génération de la vidéo',
  'translationSection.status.mergingFailed': 'Échec de l’assemblage',
  'translationSection.status.noAudio': 'Pas d’audio',
  'translationSection.status.noWords': 'Aucune parole',
  'translationSection.status.readyToGenerate': 'Prêt à générer la vidéo',
  'translationSection.status.separateBackgroundFailed': 'Échec de la séparation de l’arrière-plan',
  'translationSection.status.transcribeSegmentsFailed': 'Échec de la transcription des segments',
  'translationSection.status.transcribing': 'Transcription de la vidéo',
  'translationSection.status.transcriptionFailed': 'Échec de la transcription',
  'translationSection.status.translatingText': 'Traduction du texte',
  'translationSection.status.translationFailed': 'Échec de la traduction',
  'translationSection.status.uploadFailed': 'Échec de l’importation',
  'translationSection.status.uploading': 'Importation',
  'translationSection.status.videoGenerated': 'Vidéo générée',
  'translationSection.status.videoHasNoSound': 'La vidéo n’a pas de son',
  'translationSection.status.videoLacksSpeech': 'La vidéo ne contient pas de parole',
  'translationSection.status.voiceoverFailed': 'Échec du doublage',
  'translationSection.type': 'Traduction',
  'translationSection.zeroState.action': 'Importer une vidéo',
  'translationSection.zeroState.description':
    'Importez une vidéo et obtenez un doublage précis, des sous-titres et le clonage de voix dans plus de 135 langues.',
  'translationSection.zeroState.title': 'Traduisez votre première vidéo',
} satisfies Translation;

const it = {
  'projectsList.dropHere': 'Rilascialo qui',
  'projectsList.gridView': 'Vista griglia',
  'projectsList.listView': 'Vista elenco',
  'projectsList.menu.delete': 'Elimina',
  'projectsList.menu.download': 'Scarica video',
  'projectsList.menu.move': 'Sposta progetto',
  'projectsList.menu.rename': 'Rinomina',
  'projectsList.menu.share': 'Condividi',
  'projectsList.meta': '{type} · {time}',
  'projectsList.moreActions': 'Altre azioni per {name}',
  'projectsList.restoringFiles':
    'È tutto al sicuro. Il tuo progetto è temporaneamente non disponibile e sarà pronto tra 3–4 ore.',
  'projectsList.select': 'Seleziona {name}',
  'translationSection.status.analyzingSpeakers': 'Analisi dei parlanti',
  'translationSection.status.analyzingSrt': 'Analisi SRT',
  'translationSection.status.analyzingVideo': 'Analisi del video',
  'translationSection.status.analyzingVoices': 'Analisi delle voci',
  'translationSection.status.determineSpeakersFailed': 'Identificazione dei parlanti non riuscita',
  'translationSection.status.error': 'Errore',
  'translationSection.status.forbiddenLink': 'Link non consentito',
  'translationSection.status.generatingVideo': 'Generazione del video',
  'translationSection.status.mergingFailed': 'Unione non riuscita',
  'translationSection.status.noAudio': 'Nessun audio',
  'translationSection.status.noWords': 'Nessuna parola',
  'translationSection.status.readyToGenerate': 'Pronto per generare il video',
  'translationSection.status.separateBackgroundFailed': 'Separazione dello sfondo non riuscita',
  'translationSection.status.transcribeSegmentsFailed': 'Trascrizione dei segmenti non riuscita',
  'translationSection.status.transcribing': 'Trascrizione del video',
  'translationSection.status.transcriptionFailed': 'Trascrizione non riuscita',
  'translationSection.status.translatingText': 'Traduzione del testo',
  'translationSection.status.translationFailed': 'Traduzione non riuscita',
  'translationSection.status.uploadFailed': 'Caricamento non riuscito',
  'translationSection.status.uploading': 'Caricamento',
  'translationSection.status.videoGenerated': 'Video generato',
  'translationSection.status.videoHasNoSound': 'Il video non ha audio',
  'translationSection.status.videoLacksSpeech': 'Il video non contiene parlato',
  'translationSection.status.voiceoverFailed': 'Doppiaggio non riuscito',
  'translationSection.type': 'Traduzione',
  'translationSection.zeroState.action': 'Carica video',
  'translationSection.zeroState.description':
    'Carica un video e ottieni doppiaggio accurato, sottotitoli e clonazione vocale in oltre 135 lingue.',
  'translationSection.zeroState.title': 'Traduci il tuo primo video',
} satisfies Translation;

const ru = {
  'projectsList.dropHere': 'Отпустите здесь',
  'projectsList.gridView': 'Сетка',
  'projectsList.listView': 'Список',
  'projectsList.menu.delete': 'Удалить',
  'projectsList.menu.download': 'Скачать видео',
  'projectsList.menu.move': 'Переместить проект',
  'projectsList.menu.rename': 'Переименовать',
  'projectsList.menu.share': 'Поделиться',
  'projectsList.meta': '{type} · {time}',
  'projectsList.moreActions': 'Дополнительные действия для {name}',
  'projectsList.restoringFiles': 'Всё в сохранности. Проект временно недоступен и будет готов через 3–4 часа.',
  'projectsList.select': 'Выбрать {name}',
  'translationSection.status.analyzingSpeakers': 'Анализ спикеров',
  'translationSection.status.analyzingSrt': 'Анализ SRT',
  'translationSection.status.analyzingVideo': 'Анализ видео',
  'translationSection.status.analyzingVoices': 'Анализ голосов',
  'translationSection.status.determineSpeakersFailed': 'Не удалось определить спикеров',
  'translationSection.status.error': 'Ошибка',
  'translationSection.status.forbiddenLink': 'Недопустимая ссылка',
  'translationSection.status.generatingVideo': 'Генерация видео',
  'translationSection.status.mergingFailed': 'Не удалось собрать видео',
  'translationSection.status.noAudio': 'Нет аудио',
  'translationSection.status.noWords': 'Нет речи',
  'translationSection.status.readyToGenerate': 'Готово к генерации видео',
  'translationSection.status.separateBackgroundFailed': 'Не удалось отделить фон',
  'translationSection.status.transcribeSegmentsFailed': 'Не удалось транскрибировать сегменты',
  'translationSection.status.transcribing': 'Транскрибация видео',
  'translationSection.status.transcriptionFailed': 'Ошибка транскрибации',
  'translationSection.status.translatingText': 'Перевод текста',
  'translationSection.status.translationFailed': 'Ошибка перевода',
  'translationSection.status.uploadFailed': 'Ошибка загрузки',
  'translationSection.status.uploading': 'Загрузка',
  'translationSection.status.videoGenerated': 'Видео готово',
  'translationSection.status.videoHasNoSound': 'В видео нет звука',
  'translationSection.status.videoLacksSpeech': 'В видео нет речи',
  'translationSection.status.voiceoverFailed': 'Ошибка озвучки',
  'translationSection.type': 'Перевод',
  'translationSection.zeroState.action': 'Загрузить видео',
  'translationSection.zeroState.description':
    'Загрузите видео и получите точный дубляж, субтитры и клонирование голоса на 135+ языках.',
  'translationSection.zeroState.title': 'Переведите первое видео',
} satisfies Translation;

export const TRANSLATION_SECTION_TRANSLATIONS = defineDomainTranslations({ en, es, fr, it, pt, ru });
